import { Injectable, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, fromEvent, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, shareReplay, takeUntil } from 'rxjs/operators';

export enum eEdgeMigrationStatus {
  UpToDate = 1,
  Downloading,
  ReadyToUpdate,
}

@Injectable({
  providedIn: 'root',
})
export class ArkikStatusService implements OnDestroy {
  private readonly IS_ARKIK_ONLINE_KEY = 'isArkikOnline';
  private readonly OFFLINE_TOKEN_KEY = 'offlineToken';
  private readonly IS_SESSION_CLOSED = 'isSessionClosed';
  private readonly IS_ARKIK_LOGGED_OFFLINE = 'isArkikLoggedOffline';
  private readonly MIGRATION_STATUS = 'migrationStatus';
  private readonly IS_UNIVERSAL_LINK_RUNNING_KEY = 'isUniversalLinkRunning';

  private onDestroy$: Subject<boolean> = new Subject<boolean>();
  private readonly storage$ = fromEvent<StorageEvent>(window, 'storage');

  private arkikOnlineStatus: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  readonly arkikOnlineStatus$: Observable<boolean> = this.arkikOnlineStatus.asObservable().pipe(shareReplay(1));

  private migrationStatus: BehaviorSubject<eEdgeMigrationStatus> = new BehaviorSubject<eEdgeMigrationStatus>(
    this.getArkikMigrationStatus(),
  );
  readonly migrationStatus$: Observable<eEdgeMigrationStatus> = this.migrationStatus
    .asObservable()
    .pipe(shareReplay(1));

  private universalLinkRunningStatus: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    this.getUniversalLinkRunningStatus(),
  );
  readonly universalLinkRunningStatus$: Observable<boolean> = this.universalLinkRunningStatus
    .asObservable()
    .pipe(shareReplay(1));

  constructor() {
    this.listenToMigrationStatusFromStorage();
    this.listenToUniversalLinkRunningFromStorage();
  }

  ngOnDestroy(): void {
    this.onDestroy$.next(true);
    this.onDestroy$.complete();
  }

  private listenToMigrationStatusFromStorage(): void {
    this.storage$
      .pipe(
        takeUntil(this.onDestroy$),
        filter((event: StorageEvent) => event.key === this.MIGRATION_STATUS),
      )
      .subscribe(() => this.migrationStatus.next(this.getArkikMigrationStatus()));
  }

  private getArkikMigrationStatus(): eEdgeMigrationStatus {
    const valueMigrationStatus: string =
      localStorage.getItem(this.MIGRATION_STATUS) || eEdgeMigrationStatus.UpToDate.toString();
    const edgeMigrationStatus = +valueMigrationStatus as eEdgeMigrationStatus;
    return edgeMigrationStatus;
  }

  private listenToUniversalLinkRunningFromStorage(): void {
    this.storage$
      .pipe(
        takeUntil(this.onDestroy$),
        filter((event: StorageEvent) => event.key === this.IS_UNIVERSAL_LINK_RUNNING_KEY),
      )
      .subscribe(() => this.universalLinkRunningStatus.next(this.getUniversalLinkRunningStatus()));
  }

  private getUniversalLinkRunningStatus(): boolean {
    const itemVal = localStorage.getItem(this.IS_UNIVERSAL_LINK_RUNNING_KEY) || 'true';
    const isUniversalLinkRunning = itemVal === undefined || itemVal === null || (itemVal && itemVal === 'true');
    return isUniversalLinkRunning;
  }

  getOfflineToken(): string {
    return localStorage.getItem(this.OFFLINE_TOKEN_KEY)?.trim() ?? '';
  }

  isArkikOnlineCapable(): boolean {
    const itemIsOnline = localStorage.getItem(this.IS_ARKIK_ONLINE_KEY) || 'true';
    const isOnline = itemIsOnline === undefined || itemIsOnline === null || (itemIsOnline && itemIsOnline === 'true');
    return isOnline;
  }

  isArkikOnline(): boolean {
    const itemIsOnline = localStorage.getItem(this.IS_ARKIK_ONLINE_KEY) || 'true';
    let isOnline: boolean =
      itemIsOnline === undefined || itemIsOnline === null || (itemIsOnline && itemIsOnline === 'true');
    isOnline = isOnline && this.migrationStatus.getValue() === eEdgeMigrationStatus.UpToDate;
    this.arkikOnlineStatus.next(isOnline);
    return isOnline;
  }

  isArkikLoggedOffline(): boolean {
    const itemIsLoggedOffline = localStorage.getItem(this.IS_ARKIK_LOGGED_OFFLINE) || 'false';
    const isLoggedOffline =
      itemIsLoggedOffline === undefined ||
      itemIsLoggedOffline === null ||
      (itemIsLoggedOffline && itemIsLoggedOffline === 'true');
    return isLoggedOffline;
  }

  setArkikOnline(isOnline: boolean) {
    localStorage.setItem(this.IS_ARKIK_ONLINE_KEY, isOnline ? 'true' : 'false');
  }

  setSessionClosed() {
    localStorage.setItem(this.IS_SESSION_CLOSED, 'true');
  }

  isSessionClosed(): boolean {
    const itemVal = localStorage.getItem(this.IS_SESSION_CLOSED) || 'false';
    const isSessionClosed = itemVal === undefined || itemVal === null || (itemVal && itemVal === 'true');
    return isSessionClosed;
  }

  isUniversalLinkRunning(): boolean {
    return this.universalLinkRunningStatus.getValue();
  }
}
